import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Info, Loader2, TriangleAlert, UserPlus, X } from 'lucide-react';

import { cn } from '../../../lib/utils';
import {
  useAddPersonEvents,
  useAddPerson,
  usePersonCheck,
} from '../../../services/onboardingBoard';
import { notify, toastApiError } from '../intake/signup/signupSections';
import { formatUsPhone, usPhoneDigits } from '../../../lib/usPhone';
import { suggestEmail } from '../../../lib/emailTypos';
import PickerMenu from './PickerMenu';

// Add a person (Nic 2026-10-20). Someone a rep is talking to who isn't in
// our system goes straight onto their Signing Up (Follow-up), then gets the
// signup link. Never a second record: as the rep types, the server says
// where the person already is (lib/personLookup.js) and offers the move
// that fits instead (open, re-engage on its tab). A manager picks
// which Sales Rep gets them. Once added, the form closes and the rep works
// them from their Workspace, signup link included.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Two choices only (Nic 2026-10-22), so every message reads right for them:
// they called us, or an event we were at (picked, and it gets the credit).
// Referrals and website visitors come in through their own forms.
export const SOURCES = [
  { key: 'call', label: 'They called us' },
  { key: 'event', label: 'An event' },
];

// The pill both pickers use (Where did they come from, Who gets them).
const chipCls = (on) =>
  cn(
    'rounded-full px-3 py-1 text-xs font-semibold outline-none ring-1 focus:outline-none',
    on
      ? 'bg-slate-900 text-white ring-slate-900 dark:!bg-slate-100 dark:!text-slate-900 dark:ring-slate-100'
      : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10',
  );

// What the rep typed in the search becomes the first field it fits.
export const prefillFromSearch = (text = '') => {
  const t = String(text).trim();
  if (!t) return {};
  if (t.includes('@')) return { email: t };
  const hasLetters = /\p{L}/u.test(t);
  const d = t.replace(/\D/g, '');
  if (!hasLetters) {
    // No letters = a phone, however it is punctuated. Only a real US number
    // fills Phone (10 digits, or 11 with a leading 1), in the public forms'
    // format; anything else stays blank rather than invent a different
    // number (Nic 2026-10-20).
    const valid = d.length === 10 || (d.length === 11 && d.startsWith('1'));
    return valid ? { phone: formatUsPhone(t) } : {};
  }
  // Letters and digits mixed is neither a name nor a phone: fill nothing.
  if (d) return {};
  const [firstName, ...rest] = t.split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
};

// No Take here (Nic 2026-10-21): a search is a search. Someone already on
// file is taken the normal way, from Intake into the 10-day cadence.
const ACTION_LABEL = {
  show_nurture: 'Go to Nurture',
  show_dropped: 'Go to Dropped',
};
// Someone else's person gets no button: the box already says who has them
// (Nic 2026-10-21). Only your own opens.
const actionLabel = (m) => (m.action === 'open' ? (m.mine ? 'Open them' : null) : ACTION_LABEL[m.action]);

const INPUT =
  'h-9 w-full rounded-lg bg-white px-3 text-sm text-slate-800 outline-none ring-1 ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-sky-400 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10';
const LABEL = 'mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300';

const MatchCard = ({ match, tone, onAct, busy }) => {
  const label = match.action ? actionLabel(match) : null;
  return (
    <div
      className={cn(
        'rounded-xl px-3 py-2.5 text-sm ring-1',
        tone === 'possible'
          ? 'bg-sky-50 text-sky-900 ring-sky-200 dark:!bg-sky-500/10 dark:text-sky-100 dark:ring-sky-500/30'
          : 'bg-amber-50 text-amber-900 ring-amber-200 dark:!bg-amber-500/10 dark:text-amber-100 dark:ring-amber-500/30',
      )}
    >
      <div className="font-semibold">{match.title}</div>
      {match.detail ? <div className="mt-0.5 text-xs opacity-80">{match.detail}</div> : null}
      {/* "Is this them?": their email and phone, to compare (Nic 2026-10-21). */}
      {tone === 'possible' && (match.email || match.phone) ? (
        <div className="mt-0.5 text-xs opacity-80" data-testid="possible-contact">
          {[match.email, match.phone ? formatUsPhone(match.phone) : null].filter(Boolean).join(' · ')}
        </div>
      ) : null}
      {label ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => onAct(match)}
          className="mt-2 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 outline-none hover:bg-slate-50 focus:outline-none disabled:opacity-50 dark:!bg-white/10 dark:text-slate-100 dark:ring-white/10 dark:hover:!bg-white/15"
        >
          {label}
        </button>
      ) : null}
    </div>
  );
};

// The verdict, said plainly. `lookup` is the server's { verdict, matches }.
export const LookupCallout = ({ lookup, checking, confirmed, onConfirm, onAct, busy }) => {
  if (checking && !lookup) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Loader2 size={12} className="animate-spin" /> Checking our system...
      </div>
    );
  }
  if (!lookup?.checked && lookup?.verdict !== 'blocked' && lookup?.verdict !== 'possible') return null;
  if (lookup.verdict === 'clear') {
    return (
      <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 size={13} /> Not in our system yet. Good to add.
      </div>
    );
  }
  const [head, ...rest] = lookup.matches || [];
  const isGroup = head && ['conflict', 'unlinked'].includes(head.scenario);
  if (lookup.verdict === 'possible') {
    return (
      <div className="space-y-2" data-testid="lookup-possible">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-sky-800 dark:text-sky-200">
          <Info size={13} /> Someone with that name is already in our system. Is this them?
        </div>
        {(lookup.matches || []).map((m) => (
          <MatchCard key={`${m.leadId}-${m.userId}`} match={m} tone="possible" onAct={onAct} busy={busy} />
        ))}
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
          <input type="checkbox" checked={confirmed} onChange={(e) => onConfirm(e.target.checked)} />
          No, this is someone else. Add them anyway.
        </label>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="lookup-blocked">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 dark:text-amber-200">
        <TriangleAlert size={13} /> Already in our system. They can't be added again.
      </div>
      {isGroup ? (
        <>
          <MatchCard match={head} onAct={onAct} busy={busy} />
          <ul className="space-y-1 pl-1 text-xs text-slate-500 dark:text-slate-400">
            {rest.map((m) => (
              <li key={`${m.leadId}-${m.userId}`}>{m.title}</li>
            ))}
          </ul>
        </>
      ) : (
        (lookup.matches || []).map((m) => (
          <MatchCard key={`${m.leadId}-${m.userId}`} match={m} onAct={onAct} busy={busy} />
        ))
      )}
    </div>
  );
};

const firstOf = (name) => String(name || '').trim().split(/\s+/)[0] || '';

// What the server checks for a form: a full phone or a valid email, else
// nothing to check yet.
const checkParamsOf = (form) => {
  const phone = usPhoneDigits(form.phone);
  const email = String(form.email || '').trim();
  const emailValid = !!email && EMAIL_RE.test(email);
  if (phone.length !== 10 && !emailValid) return null;
  return {
    firstName: (form.firstName || '').trim(),
    lastName: (form.lastName || '').trim(),
    phone: phone.length === 10 ? phone : '',
    email: emailValid ? email : '',
  };
};

// The form itself. Mounted fresh on every open (AddPersonDialog below), so
// it never shows the last person for a frame before clearing (Nic
// 2026-10-20: "it first loads in whatever the modal last said").
const AddPersonPanel = ({
  prefill = {},
  viewerUserId,
  owners = null,
  onClose,
  onOpenMatch,
  onDone,
}) => {
  const isManager = Array.isArray(owners);
  const [form, setForm] = useState(() => {
    const meIsOwner = isManager && owners.some((o) => o.userId === viewerUserId);
    return {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      source: '',
      eventPartnerId: '',
      notes: '',
      ownerUserId: isManager ? (meIsOwner ? viewerUserId : '') : viewerUserId,
      ...prefill,
      ...(prefill.phone ? { phone: formatUsPhone(prefill.phone) } : {}),
    };
  });
  // The events for "An event", loaded once they pick it.
  const eventsQ = useAddPersonEvents({ enabled: form.source === 'event' });
  const events = eventsQ.data?.events || [];
  const [confirmed, setConfirmed] = useState(false);
  // The prefilled number is checked right away; typing waits a beat.
  const [debounced, setDebounced] = useState(() => checkParamsOf(form));
  const [raced, setRaced] = useState(null);

  const phone = usPhoneDigits(form.phone);
  const email = String(form.email || '').trim();
  // Required (Nic 2026-10-21); "doesn't look right" only once something is typed.
  const emailOk = !email || EMAIL_RE.test(email);
  const hasEmail = !!email && EMAIL_RE.test(email);
  // A provider typo (gmail.co) asks "Did you mean ...?" until the rep picks
  // the fix or says it's right as typed.
  const [emailConfirmed, setEmailConfirmed] = useState(null);
  const suggestion = email && emailOk ? suggestEmail(email) : null;
  const typoOpen = !!suggestion && emailConfirmed !== email;

  useEffect(() => {
    const next = checkParamsOf(form);
    const t = setTimeout(() => setDebounced(next), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.firstName, form.lastName, form.phone, form.email]);

  const check = usePersonCheck(debounced || {}, { enabled: !!debounced });
  // Only a verdict for what is in the boxes RIGHT NOW counts: mid-typing,
  // the last number's answer must not show (or let Add through).
  const current = checkParamsOf(form);
  const inSync = JSON.stringify(current) === JSON.stringify(debounced);
  const lookup = raced || (current && inSync ? check.data || null : null);
  const checking = !!current && !raced && (!inSync || check.isFetching);
  // The server's DNS answer for what is typed now: false = can't take mail.
  const undeliverable = !!email && lookup?.emailDeliverable === false;
  const blocked = lookup?.verdict === 'blocked';
  const possible = lookup?.verdict === 'possible';

  const addPerson = useAddPerson();
  const busy = addPerson.isLoading;

  const ownerName = useMemo(() => {
    if (!form.ownerUserId || form.ownerUserId === viewerUserId) return null;
    return (owners || []).find((o) => o.userId === form.ownerUserId)?.name || 'their rep';
  }, [form.ownerUserId, owners, viewerUserId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setRaced(null);
    if (['firstName', 'lastName', 'phone', 'email'].includes(key)) setConfirmed(false);
  };

  const ready =
    form.firstName?.trim() &&
    form.lastName?.trim() &&
    phone.length === 10 &&
    hasEmail &&
    !typoOpen &&
    !undeliverable &&
    form.source &&
    (form.source !== 'event' || !!form.eventPartnerId) &&
    form.ownerUserId &&
    !blocked &&
    (!possible || confirmed) &&
    // Checked for what is typed now (a failed check falls back to the
    // server's own re-check on Add).
    !checking &&
    (!!lookup || check.isError) &&
    !busy;

  const onAct = (m) => {
    onOpenMatch?.(m);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    try {
      const res = await addPerson.mutateAsync({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone,
        email,
        source: form.source,
        ...(form.source === 'event' ? { eventPartnerId: form.eventPartnerId } : {}),
        notes: form.notes?.trim() || '',
        ownerUserId: form.ownerUserId,
        ...(possible && confirmed ? { confirmPossible: true } : {}),
      });
      // No after-screen (Nic 2026-10-21): the form closes and says where
      // they went. The link, booking and the rest happen in their Workspace.
      const first = form.firstName.trim();
      const mine = res.ownerUserId === viewerUserId;
      notify.success(
        mine
          ? `${first} is on your Signing Up, in Follow-up.`
          : `${first} is on ${firstOf(ownerName)}'s Signing Up, in Follow-up.`,
      );
      onDone?.(res);
    } catch (err) {
      const body = err?.response?.data;
      // Someone beat us to it, or the name check came back: show it inline.
      if (err?.response?.status === 409 && body?.lookup) {
        setRaced({ ...body.lookup, checked: true });
        return;
      }
      toastApiError(err, "We couldn't add them.");
    }
  };

  const body = (
    <form onSubmit={submit} className="space-y-3" data-testid="add-person-form">
      <div className="flex items-center justify-between">
        <h3 id="add-person-title" className="flex items-center gap-2 text-base font-bold text-slate-800 dark:text-slate-100">
          <UserPlus size={17} /> Add someone to <span className="text-sky-600 dark:text-sky-400">Signing Up</span>
        </h3>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 outline-none hover:bg-slate-100 focus:outline-none dark:hover:!bg-white/10"
        >
          <X size={15} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="ap-first" className={LABEL}>First name</label>
          <input id="ap-first" className={INPUT} value={form.firstName || ''} onChange={set('firstName')} autoComplete="off" />
        </div>
        <div>
          <label htmlFor="ap-last" className={LABEL}>Last name</label>
          <input id="ap-last" className={INPUT} value={form.lastName || ''} onChange={set('lastName')} autoComplete="off" />
        </div>
      </div>
      <div>
        <label htmlFor="ap-phone" className={LABEL}>Phone</label>
        <input
          id="ap-phone"
          className={INPUT}
          inputMode="tel"
          placeholder="(904) 555-1212"
          value={form.phone || ''}
          // The public forms' guided input (lib/usPhone): formats as you
          // type, stops at 10 digits, drops a pasted +1.
          onChange={(e) => set('phone')(formatUsPhone(e.target.value))}
          autoComplete="off"
        />
      </div>
      <div>
        <label htmlFor="ap-email" className={LABEL}>
          Email
        </label>
        <input id="ap-email" className={INPUT} inputMode="email" value={form.email || ''} onChange={set('email')} autoComplete="off" />
        {!emailOk ? <div className="mt-1 text-xs text-rose-600 dark:text-rose-300">That email doesn't look right.</div> : null}
        {emailOk && (typoOpen || undeliverable) ? (
          <div
            data-testid="email-check"
            className="mt-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-900 ring-1 ring-amber-200 dark:!bg-amber-500/10 dark:text-amber-100 dark:ring-amber-500/30"
          >
            {undeliverable ? (
              <div className="font-semibold">That email can't receive mail. Check it and try again.</div>
            ) : null}
            {suggestion ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>
                  Did you mean <span className="font-semibold">{suggestion}</span>?
                </span>
                <button
                  type="button"
                  onClick={() => set('email')(suggestion)}
                  className="rounded-md bg-white px-2 py-0.5 font-semibold text-slate-800 ring-1 ring-amber-300 outline-none hover:bg-amber-100 focus:outline-none dark:!bg-white/10 dark:text-slate-100 dark:ring-white/15"
                >
                  Use that
                </button>
                {!undeliverable ? (
                  <button
                    type="button"
                    onClick={() => setEmailConfirmed(email)}
                    className="font-semibold text-amber-800 underline-offset-2 outline-none hover:underline focus:outline-none dark:text-amber-200"
                  >
                    It's right as typed
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <LookupCallout
        lookup={lookup}
        checking={checking}
        confirmed={confirmed}
        onConfirm={setConfirmed}
        onAct={onAct}
        busy={busy}
      />

      <div>
        <span className={LABEL}>Where did they come from?</span>
        <div className="flex flex-wrap gap-1.5">
          {SOURCES.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-pressed={form.source === s.key}
              onClick={() => set('source')(s.key)}
              className={chipCls(form.source === s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
        {form.source === 'event' ? (
          <div className="mt-1.5">
            <PickerMenu
              label={events.find((ev) => ev.partnerId === form.eventPartnerId)?.name || 'Which event?'}
              title="Which event"
              options={events.map((ev) => ({ value: ev.partnerId, label: ev.name }))}
              selectedValue={form.eventPartnerId}
              onPick={(v) => set('eventPartnerId')(v)}
              emptyLabel={eventsQ.isLoading ? 'Loading events...' : 'No events yet'}
              menuClassName="rounded-xl"
              itemClassName="px-3 py-2 text-sm"
              triggerClassName={cn(INPUT, 'text-left', !form.eventPartnerId && '!text-slate-400 dark:!text-slate-500')}
            />
          </div>
        ) : null}
      </div>
      {isManager ? (
        <div>
          <span id="ap-owner-label" className={LABEL}>Who gets them?</span>
          <div role="group" aria-labelledby="ap-owner-label" className="flex flex-wrap gap-1.5">
            {owners.map((o) => (
              <button
                key={o.userId}
                type="button"
                aria-pressed={form.ownerUserId === o.userId}
                onClick={() => set('ownerUserId')(o.userId)}
                className={chipCls(form.ownerUserId === o.userId)}
              >
                {o.userId === viewerUserId ? `${o.name} (me)` : o.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div>
        <label htmlFor="ap-notes" className={LABEL}>
          Notes <span className="font-normal text-slate-400">(optional)</span>
        </label>
        {/* Grows as they type, up to about 6 lines (Nic 2026-10-20: the
            corner handle looked draggable and wasn't). */}
        <textarea
          id="ap-notes"
          rows={2}
          className={cn(INPUT, 'h-auto max-h-36 resize-none py-2')}
          value={form.notes || ''}
          onChange={(e) => {
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
            set('notes')(e);
          }}
        />
      </div>
      <button
        type="submit"
        disabled={!ready}
        className="w-full rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 dark:!bg-slate-100 dark:!text-slate-900"
      >
        {addPerson.isLoading
          ? 'Adding...'
          : ownerName
            ? <>Add to {firstOf(ownerName)}'s <span className="text-sky-300 dark:text-sky-700">Signing Up</span></>
            : <>Add to my <span className="text-sky-300 dark:text-sky-700">Signing Up</span></>}
      </button>
    </form>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]"
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-person-title"
        onMouseDown={(e) => e.stopPropagation()}
        // p-[20px], not p-5: Bootstrap's .p-5 (3rem !important) won and
        // made the edge thick (Nic 2026-10-20).
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-[20px] shadow-[0_20px_60px_rgba(15,23,42,0.3)] dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10"
      >
        {body}
      </div>
    </div>,
    document.body,
  );
};

// Closed = not mounted, so every open starts clean.
const AddPersonDialog = ({ open, ...props }) => (open ? <AddPersonPanel {...props} /> : null);

export default AddPersonDialog;
